/* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useEffect, useRef, useState} from 'react'
import {ContractPreview} from '../components/ContractPreview'
import {useAppDispatch, useAppSelector} from '../../store/Store'
import {
  selectContract,
  selectContractFiltersSelection,
  selectContracts,
  selectContractsPagination,
  selectContractLoading,
  setContractFiltersSelection,
} from '../Contract.slice'
import {
  useContractGetByIdMutation,
  useContractLoadMoreMutation,
  useContractSearchMutation,
} from '../Contract.api'
import {ContractListItem} from '../components/ContractListItem'
import {SearchList} from '../../../common/component/SearchList'
import {IContractFilter} from '../Contract.model'
import { IFilterDto } from '../../../common/model/Core.model'
import {TagInputSearchControls} from '../../../common/component/TagInputSearchControls'
import { useIntl } from 'react-intl'
import { PageLink, PageTitle } from '../../../../_metronic/layout/core/PageData'
import { getEntry, removeEntry, setEntry } from '../../../service/LocalStorage.service'

const CONTRACT_FILTERS_LS_KEY = 'contract_filtersSelection'

const ContractDashboardPage: FC = () => {
  const intl = useIntl()
  const breadCrumbs: Array<PageLink> = [
    {
      title: intl.formatMessage({id: 'BREADCRUMBS.CONTRACTS'}),
      path: '/contracts',
      isSeparator: false,
      isActive: false,
    }
  ]

  const didFetchInitialData = useRef(false)
  const didFetchNextPage = useRef(false)
  const didFetchSearch = useRef(false)

  const filterKeys = {
    defaultKey: IContractFilter.CLIENT,
    defaultKeyList: [
      IContractFilter.ID,
      IContractFilter.CLIENT,
      IContractFilter.STATUS,
      IContractFilter.DOCUMENT,
      IContractFilter.SERVICE,
      IContractFilter.FREQUENCY,
      IContractFilter.ADDRESS,
    ],
  }
  const [isSearchLoading, setIsSearchLoading] = useState(false)
  const [isSearchControlEnabled, setIsSearchControlEnabled] = useState(false)
  const [filterSuggestions, setFilterSuggestions] = useState<string[]>([])
  const [rowSelected, setRowSelected] = useState<string | undefined>(undefined)

  const dispatch = useAppDispatch()
  const filters = useAppSelector(selectContractFiltersSelection)

  const sanitizePersistedFilters = (candidateFilters: unknown): IFilterDto[] => {
    if (!Array.isArray(candidateFilters)) {
      return []
    }
    return candidateFilters.filter((item): item is IFilterDto => {
      if (!item || typeof item !== 'object') {
        return false
      }
      const filter = item as IFilterDto
      return (
        typeof filter.id === 'string' &&
        filterKeys.defaultKeyList.includes(filter.id as IContractFilter) &&
        typeof filter.value === 'string' &&
        typeof filter.editable === 'boolean'
      )
    })
  }

  const persistFiltersSelection = (nextFilters: IFilterDto[]) => {
    dispatch(setContractFiltersSelection(nextFilters))
    if (nextFilters.length > 0) {
      setEntry(CONTRACT_FILTERS_LS_KEY, JSON.stringify(nextFilters))
    } else {
      removeEntry(CONTRACT_FILTERS_LS_KEY)
    }
  }

  const setFilters = (nextFilters: IFilterDto[]) => {
    persistFiltersSelection(nextFilters)
  }

  const [contractSearch] = useContractSearchMutation()
  const [contractLoadMore] = useContractLoadMoreMutation()
  const [contractGetById] = useContractGetByIdMutation()

  const contracts = useAppSelector(selectContracts)
  const contractsPagination = useAppSelector(selectContractsPagination)
  const contract = useAppSelector(selectContract)
  const contractLoading = useAppSelector(selectContractLoading)

  useEffect(() => {
    let initialFilters: IFilterDto[] | undefined
    const savedFilters = getEntry(CONTRACT_FILTERS_LS_KEY)
    if (savedFilters) {
      try {
        const parsedFilters = JSON.parse(savedFilters)
        const sanitizedFilters = sanitizePersistedFilters(parsedFilters)
        persistFiltersSelection(sanitizedFilters)
        initialFilters = sanitizedFilters
      } catch {
        persistFiltersSelection([])
      }
    }

    if (initialFilters && initialFilters.length > 0) {
      const filterIds = initialFilters.map((filter) => filter.id)
      setFilterSuggestions(filterKeys.defaultKeyList.filter((key) => !filterIds.includes(key)))
    } else {
      setFilterSuggestions(filterKeys.defaultKeyList)
    }

    fetchInitialData(initialFilters)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchInitialData = async (filtersOverride?: IFilterDto[]) => {
    const requestFilters = filtersOverride ?? filters
    try {
      setIsSearchLoading(true)
      if (!didFetchInitialData.current) {
        const request: any = {}
        if (requestFilters.length > 0) {
          request.filters = requestFilters
        }
        const response: any = await contractSearch(request)
        if (response.data) {
          setIsSearchControlEnabled(true)
          if (response.data.filters) {
            const filterIds = response.data.filters.map((filter) => filter.id)
            persistFiltersSelection(response.data.filters)
            setFilterSuggestions(filterKeys.defaultKeyList.filter((key) => !filterIds.includes(key)))
          } else {
            const filterIds = requestFilters.map((filter) => filter.id)
            setFilterSuggestions(filterKeys.defaultKeyList.filter((key) => !filterIds.includes(key)))
          }
        }
      }
    } finally {
      setIsSearchLoading(false)
    }
  }

  const nextPageOnClickHandler = async () => {
    try {
      setIsSearchLoading(true)
      if (!didFetchNextPage.current) {
        await contractLoadMore({
          pagination: {
            limit: contractsPagination?.limit,
            sort: contractsPagination?.sort,
            page: contractsPagination?.page,
          },
          filters: filters,
        })
      }
    } finally {
      setIsSearchLoading(false)
    }
  }

  const searchOnClickHandler = async () => {
    try {
      setIsSearchLoading(true)
      if (!didFetchSearch.current) {
        await contractSearch({
          pagination: {
            sort: contractsPagination?.sort,
          },
          filters: filters,
        })
      }
    } finally {
      setIsSearchLoading(false)
    }
  }

  const resetSearchOnClickHandler = async () => {
    persistFiltersSelection([])
    setFilterSuggestions(filterKeys.defaultKeyList)
    await fetchInitialData([])
  }

  const rowOnClickHandler = async (id: string) => {
    setRowSelected(id)
    if (!contract || contract.id !== id) {
      await contractGetById({id, fields: 'LIMITED'})
    }
  }

  return (
    <>
      <PageTitle breadcrumbs={breadCrumbs}></PageTitle>
      <div className='flex mt-10'>
        <div className='row g-5'>
          <SearchList
            loading={isSearchLoading}
            rowSelected={rowSelected}
            nextPage={contractsPagination?.page}
            nextPageOnClickHandler={nextPageOnClickHandler}
            searchControlsComponent={
              <TagInputSearchControls
                loading={isSearchLoading}
                enabled={isSearchControlEnabled}
                filterKeys={filterKeys}
                filterSuggestions={filterSuggestions}
                filters={filters}
                setFilterSuggestions={setFilterSuggestions}
                setFilters={setFilters}
                createOnClickPath='/contracts/new'
                createOnClickOpenNew={false}
                searchOnClickHandler={searchOnClickHandler}
                resetSearchResetOnClickHandler={resetSearchOnClickHandler}
              ></TagInputSearchControls>
            }
            listComponent={contracts.map((item, index) => (
              <div key={index}>
                <ContractListItem
                  data={item}
                  rowSelected={rowSelected}
                  onRowClick={rowOnClickHandler}
                ></ContractListItem>
                {index !== 0 && <div className='separator'></div>}
              </div>
            ))}
          ></SearchList>

          {rowSelected && (
            <ContractPreview
              contract={contract}
              loading={contractLoading}
              setRowSelected={setRowSelected}
            ></ContractPreview>
          )}
        </div>
      </div>
    </>
  )
}

export {ContractDashboardPage}
